/**
 * Helper to normalize image URLs for display
 * Detects HEIC files and converts them on-the-fly
 */

import { resolveStorageUrl } from "@/lib/storage/public-url";

// Cache pour éviter de convertir la même image HEIC plusieurs fois
// Keyed by the RESOLVED absolute URL so a relative path and its absolute form
// share one conversion (relative-storage-path migration).
const heicConversionCache = new Map<string, Promise<string>>();

export function getPendingHeicConversion(url: string): Promise<string> | undefined {
  return heicConversionCache.get(resolveStorageUrl(url));
}

/**
 * `true` when `url` points at a HEIC/HEIF file the browser cannot decode and
 * `convertHeicUrlToJpeg` must therefore transcode first.
 *
 * **Tests the extension of the PATH, never of the whole string.** The same
 * image reaches this predicate in three shapes, and only the first two survive
 * a naive `endsWith('.heic')`:
 *
 * | shape | example | old | new |
 * |---|---|---|---|
 * | bucket-relative path | `posts/1690-a.heic` | ✅ | ✅ |
 * | absolute `object/public` | `…/object/public/kraahl/posts/1690-a.heic` | ✅ | ✅ |
 * | `render/image` + query | `…/render/image/public/kraahl/posts/1690-a.heic?width=828&…` | ❌ | ✅ |
 *
 * The third row was a landmine for the whole image-pipeline standardisation,
 * not a cosmetic gap: `toSupabasePublicImageThumbUrl` appends `?width=…&…`, so
 * the moment a surface was moved onto a preset URL every HEIC on it silently
 * stopped being detected and rendered as a broken image. That is why
 * `components/search/simple-post-card.tsx` had to stay on the Next optimizer —
 * the Supabase path and the HEIC path were mutually exclusive, which is the
 * actual root of the two forked image wrappers (handoff §7).
 *
 * **The widening cannot create a false positive.** It still requires a literal
 * `.heic`/`.heif` extension; all that changed is WHERE the extension is looked
 * for. Nothing that is not a HEIC file can start converting — the only URLs
 * whose answer flips are ones that were HEIC all along and were being missed.
 *
 * `blob:` / `data:` are rejected up front. Those are bytes already decoded in
 * memory — a `blob:` src is typically the OUTPUT of a previous conversion, and
 * re-entering the conversion path on it would be a loop.
 *
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §9 step 8
 * @see tests/unit/is-heic-url.test.ts — the pin over every shape above, plus
 *      the `blob:` / `data:` / near-miss rejections
 */
export function isHeicUrl(url: string): boolean {
  if (!url) return false;
  // Already-decoded bytes in memory; never re-enter conversion on them.
  if (/^(?:blob|data):/i.test(url)) return false;
  // Drop the fragment, then the query — what remains is the path whose last
  // segment carries the extension. Both are `indexOf`-sliced rather than
  // `new URL()`-parsed because the input may legitimately be a bucket-relative
  // path (`posts/a.heic`), which is not a parseable absolute URL.
  const hashAt = url.indexOf('#');
  const withoutHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const queryAt = withoutHash.indexOf('?');
  const path = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
  return /\.hei[cf]$/i.test(path);
}

/**
 * Convert HEIC image from URL to JPEG blob URL
 * This allows old HEIC images to be displayed in browsers
 */
export async function convertHeicUrlToJpeg(rawUrl: string): Promise<string> {
  if (typeof window === 'undefined') {
    throw new Error('HEIC conversion only works client-side');
  }

  // `fetch()` below cannot download a bucket-relative path — resolve first.
  // Idempotent, so callers that already resolved are unaffected.
  // @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L3
  const url = resolveStorageUrl(rawUrl);

  // Check cache first to avoid converting the same image multiple times
  if (heicConversionCache.has(url)) {
    console.log('♻️ Using cached HEIC conversion for:', url);
    try {
      return await heicConversionCache.get(url)!;
    } catch (cachedError) {
      // If cached conversion failed, remove from cache and try again
      console.warn('⚠️ Cached conversion failed, retrying:', url);
      heicConversionCache.delete(url);
    }
  }
  
  // Create conversion promise and cache it
  const conversionPromise = (async () => {
    try {
      // Download the HEIC file
      console.log('🔄 Starting HEIC conversion for:', url);
    
    const response = await fetch(url, { 
      mode: 'cors',
      credentials: 'omit'
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Failed to fetch HEIC file: ${response.status} ${response.statusText}. ${errorText}`);
    }
    
    const blob = await response.blob();
    console.log('📥 Downloaded HEIC blob:', blob.size, 'bytes, type:', blob.type);
    
    if (blob.size === 0) {
      throw new Error('Downloaded HEIC file is empty');
    }
    
    // Validate blob type (might be empty or wrong content-type)
    // Note: HEIC files might have application/octet-stream or no content-type
    if (!blob.type && blob.size > 0) {
      console.warn('⚠️ Blob has no content-type, proceeding anyway (might be HEIC)');
    }
    
    // Check if blob is actually HEIC by checking first bytes (magic number)
    // HEIC files typically start with specific byte sequences
    try {
      const firstBytes = await blob.slice(0, 12).arrayBuffer();
      const bytes = new Uint8Array(firstBytes);
      const hexString = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
      
      // HEIC magic numbers: ftyp box with heic/heif brands
      const isLikelyHeic = hexString.includes('6674797068656963') || // ftyp heic
                           hexString.includes('6674797068656966') || // ftyp heif
                           hexString.includes('667479706d697631');  // ftyp mif1
      
      if (!isLikelyHeic && blob.type !== 'image/heic' && blob.type !== 'image/heif' && blob.type !== 'application/octet-stream') {
        console.warn('⚠️ Downloaded file might not be a valid HEIC file (magic number check failed)');
        console.log('First 12 bytes (hex):', hexString);
        // Continue anyway - heic2any will handle it
      } else {
        console.log('✅ Magic number check passed - file appears to be HEIC');
      }
    } catch (magicCheckError) {
      console.warn('⚠️ Could not check HEIC magic number:', magicCheckError);
      // Continue anyway
    }
    
    // Import heic-to dynamically (uses libheif 1.20.2 - better HEIC support)
    let heicTo;
    try {
      const heicToModule = await import('heic-to');
      heicTo = heicToModule.heicTo || heicToModule.default || heicToModule;
      console.log('✅ heic-to loaded, starting conversion...');
    } catch (importError) {
      const importErr = importError instanceof Error ? importError.message : String(importError);
      throw new Error(`Failed to load heic-to library: ${importErr}`);
    }
    
    if (!heicTo || typeof heicTo !== 'function') {
      throw new Error('heic-to is not a valid function');
    }
    
      // Convert HEIC to JPEG using heic-to
      // heic-to uses libheif 1.20.2 which has better support for recent HEIC formats
      let convertedBlob;
      const heicConversionPromise = heicTo({
        blob,
        type: 'image/jpeg',
        quality: 0.85,
      });
      
      // Add timeout to catch hanging conversions (30 seconds for heic-to which may take longer)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('HEIC conversion timeout after 30s')), 30000);
      });
      
      try {
        convertedBlob = await Promise.race([heicConversionPromise, timeoutPromise]);
        console.log('🖼️ Conversion complete, result:', Array.isArray(convertedBlob) ? `array of ${convertedBlob.length}` : 'single blob');
      } catch (convertError) {
      // Try to get more details from the error
      let errorDetails: any = {};
      let errorMessage = '';
      
      if (convertError instanceof Error) {
        errorMessage = convertError.message || convertError.toString() || 'Unknown error';
        errorDetails = {
          name: convertError.name || 'Error',
          message: errorMessage,
          stack: convertError.stack?.substring(0, 500), // Limit stack trace
        };
      } else if (convertError && typeof convertError === 'object') {
        // Handle plain error objects
        try {
          errorDetails = { ...convertError };
          const errorObj = convertError as any;
          // Check if it's a heic-to error object
          if (errorObj.code && errorObj.message) {
            errorMessage = `code: ${errorObj.code}, message: ${errorObj.message}`;
          } else if (errorObj.message) {
            errorMessage = String(errorObj.message);
          } else {
            // Try to stringify but handle circular references
            try {
              errorMessage = JSON.stringify(convertError);
            } catch {
              errorMessage = String(convertError);
            }
          }
        } catch {
          errorMessage = String(convertError);
          errorDetails = { error: errorMessage };
        }
      } else {
        errorMessage = String(convertError || 'Unknown conversion error');
        errorDetails = { error: errorMessage };
      }
      
      // Ensure we have a valid error message
      if (!errorMessage || errorMessage === '{}') {
        errorMessage = 'HEIC conversion failed with unknown error';
      }
      
      // Check if it's the "format not supported" error from libheif
      const isFormatNotSupported = 
        errorMessage.includes('ERR_LIBHEIF format not supported') ||
        errorMessage.includes('format not supported') ||
        errorMessage.includes('compression format has not been built') ||
        errorMessage.includes('code:2');
      
      if (isFormatNotSupported) {
        console.warn('⚠️ HEIC format not supported by libheif - this is a rare unsupported variant');
        // Note: We don't try server-side with Sharp anymore as it has the same limitation
        // These files may need to be re-uploaded in a different format
        throw new Error(`HEIC_FORMAT_NOT_SUPPORTED: This HEIC file format is not supported by any converter (codec 11.6003). The file may need to be re-uploaded in JPEG/PNG format.`);
      }
      
      // Check if it's a validation error (blob might not be HEIC)
      const errorStr = JSON.stringify(errorDetails);
      if (errorStr === '{}' || !errorStr || errorStr === '{"error":"undefined"}') {
        throw new Error('heic-to returned empty error - file might not be a valid HEIC or conversion library failed');
      }
      
      throw new Error(`heic-to conversion failed: ${errorStr}`);
    }
    
    // Handle both single blob and array of blobs
    const finalBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
    
    if (!finalBlob) {
      throw new Error('Conversion returned empty result');
    }
    
    // Validate final blob
    if (!(finalBlob instanceof Blob)) {
      throw new Error(`Conversion returned invalid result type: ${typeof finalBlob}`);
    }
    
    // Create blob URL for display
    const blobUrl = URL.createObjectURL(finalBlob);
    console.log('✅ HEIC converted successfully, blob URL created');
    return blobUrl;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorDetails = error instanceof Error ? {
      name: error.name,
      message: error.message,
      stack: error.stack
    } : error;
    
    // Don't log timeout errors as errors - they're expected for large files or slow conversions
    if (errorMessage.includes('timeout')) {
      console.warn('⚠️ HEIC conversion timeout (this is expected for large files):', {
        url,
        error: errorMessage,
      });
    } else {
      // Only log actual errors, not timeouts
      console.error('❌ Error converting HEIC URL to JPEG:', {
        url,
        error: errorMessage,
        details: errorDetails,
        timestamp: new Date().toISOString()
      });
    }
    
      // Re-throw with more context
      throw new Error(`HEIC conversion failed: ${errorMessage}`);
    }
  })();
  
  heicConversionCache.set(url, conversionPromise);
  
  setTimeout(() => {
    heicConversionCache.delete(url);
    try {
      conversionPromise.then(blobUrl => {
        if (blobUrl && blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl);
        }
      }).catch(() => {});
    } catch {}
  }, 5 * 60 * 1000);
  
  return conversionPromise;
}

/**
 * Get a display-safe image URL
 * Returns the URL as-is (components will handle HEIC conversion on display)
 */
export function getDisplayImageUrl(url: string): string {
  if (!url) return url;
  return url;
}

/**
 * Normalize an array of image URLs
 */
export function normalizeImageUrls(urls: string[]): string[] {
  return urls.map(getDisplayImageUrl);
}

